import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import NavBar from "@/components/NavBar";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import MemberDashboard from "@/pages/MemberDashboard";
import NewOrderPage from "@/pages/NewOrderPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMembersPage from "@/pages/AdminMembersPage";
import AdminMemberDetailPage from "@/pages/AdminMemberDetailPage";
import AdminMemberImportPage from "@/pages/AdminMemberImportPage";
import AdminStaffPage from "@/pages/AdminStaffPage";
import ClubProfilePage from "@/pages/ClubProfilePage";
import ProfilePage from "@/pages/ProfilePage";
import CrestProfilePage from "@/pages/CrestProfilePage";
import AdminIntelligencePage from "@/pages/AdminIntelligencePage";
import WishlistPage from "@/pages/WishlistPage";
import SuperAdminLoginPage, { SuperAdminResetPasswordPage } from "@/pages/SuperAdminLoginPage";
import SuperAdminDashboardPage from "@/pages/SuperAdminDashboardPage";
import SuperAdminJoinAnalyticsPage from "@/pages/SuperAdminJoinAnalyticsPage";
import SuperAdminClubsPage from "@/pages/SuperAdminClubsPage";
import SuperAdminClubDetailPage from "@/pages/SuperAdminClubDetailPage";
import SuperAdminArchivePage from "@/pages/SuperAdminArchivePage";
import SuperAdminDemandPage from "@/pages/SuperAdminDemandPage";
import SuperAdminInvitationsPage from "@/pages/SuperAdminInvitationsPage";
import SuperAdminMergePage from "@/pages/SuperAdminMergePage";
import SuperAdminPlansPage from "@/pages/SuperAdminPlansPage";
import AdminInvitationsPage from "@/pages/AdminInvitationsPage";
import AdminAuditPage from "@/pages/AdminAuditPage";
import StaffActivatePage from "@/pages/StaffActivatePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PublicJoinPage from "@/pages/PublicJoinPage";
import AdminPendingMembershipsPage from "@/pages/AdminPendingMembershipsPage";
import VendorsMarketingPage from "@/pages/marketing/VendorsMarketingPage";
import MembersMarketingPage from "@/pages/marketing/MembersMarketingPage";
import ClubsMarketingPage from "@/pages/marketing/ClubsMarketingPage";
import PricingPage from "@/pages/marketing/PricingPage";
import ClubPricingPage from "@/pages/marketing/ClubPricingPage";
import VendorPricingPage from "@/pages/marketing/VendorPricingPage";
import AboutPage from "@/pages/marketing/AboutPage";
import ContactPage from "@/pages/marketing/ContactPage";
import VendorApplyPage from "@/pages/marketing/VendorApplyPage";
import VendorDashboardPage from "@/pages/vendor_portal/VendorDashboardPage";
import VendorPlaceholderPage from "@/pages/vendor_portal/VendorPlaceholderPage";
import VendorSettingsPage from "@/pages/vendor_portal/VendorSettingsPage";
import VendorBackOfficeLoginPage from "@/pages/VendorBackOfficeLoginPage";
import VendorSignInPage from "@/pages/VendorSignInPage";
import ClubVendorDirectoryPage from "@/pages/ClubVendorDirectoryPage";
import VendorLinkPage from "@/pages/VendorLinkPage";
import VendorCompanyProfilePage from "@/pages/vendor_portal/VendorCompanyProfilePage";
import VendorCatalogsPage from "@/pages/vendor_portal/VendorCatalogsPage";
import VendorCatalogDetailPage from "@/pages/vendor_portal/VendorCatalogDetailPage";
import VendorRequestsPage from "@/pages/vendor_portal/VendorRequestsPage";
import VendorRequestDetailPage from "@/pages/vendor_portal/VendorRequestDetailPage";
import VendorMessagesPage from "@/pages/vendor_portal/VendorMessagesPage";
import AdminCatalogsPage from "@/pages/AdminCatalogsPage";
import AdminCatalogViewPage from "@/pages/AdminCatalogViewPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import MemberCollectionsPage from "@/pages/MemberCollectionsPage";
import MemberCollectionDetailPage, { MemberProductDetailPage } from "@/pages/MemberCollectionDetailPage";
import SuperAdminVendorsPage from "@/pages/SuperAdminVendorsPage";
import SuperAdminSupportPage from "@/pages/SuperAdminSupportPage";
import SupportTicketDetailPage from "@/pages/SupportTicketDetailPage";
import MyTicketsPage from "@/pages/MyTicketsPage";
import CommandCenterPlaceholder from "@/pages/command_center/CommandCenterPlaceholder";
import ScrollToTop from "@/components/ScrollToTop";
import { homePathForRole } from "@/lib/roleRouter";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="overline">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(user.role)} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback (session_id in URL fragment) FIRST.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
        <Route path="/super-admin/reset-password" element={<SuperAdminResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/join/:slug" element={<PublicJoinPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* iter72 · Secure Vendor Link — anonymous, token-authorized. */}
        <Route path="/v/:token" element={<VendorLinkPage />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute role="member">
              <NavBar />
              <MemberDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/new"
          element={
            <ProtectedRoute role="member">
              <NavBar />
              <NewOrderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute role="member">
              <NavBar />
              <OrderHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/vendor-contacts"
          element={
            <ProtectedRoute role="club_admin">
              <ClubVendorDirectoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute>
              <NavBar />
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminMembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members/import"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminMemberImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/pending-memberships"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminPendingMembershipsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members/:userId"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminMemberDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminStaffPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/club-profile"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <ClubProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/intelligence"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminIntelligencePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <NavBar />
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crest-profile"
          element={
            <ProtectedRoute role="member">
              <NavBar />
              <CrestProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/wishlist"
          element={
            <ProtectedRoute role="member">
              <NavBar />
              <WishlistPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/super-admin"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/join-analytics"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminJoinAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/clubs"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminClubsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route
          path="/super-admin/clubs/:clubId"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminClubDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/archive"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminArchivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/demand"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminDemandPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/invitations"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminInvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/merge"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminMergePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin/plans"
          element={
            <ProtectedRoute role="super_admin">
              <SuperAdminPlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invitations"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminInvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute role="club_admin">
              <NavBar />
              <AdminAuditPage />
            </ProtectedRoute>
          }
        />
        <Route path="/staff/activate" element={<StaffActivatePage />} />

        {/* --- Prompt 2: Marketing / Multi-Audience Landing Pages --- */}
        <Route path="/vendors" element={<VendorsMarketingPage />} />
        <Route path="/vendors/apply" element={<VendorApplyPage />} />
        <Route path="/vendors/back-office" element={<VendorBackOfficeLoginPage />} />
        <Route path="/vendor/login" element={<VendorSignInPage />} />
        <Route path="/vendors/login" element={<VendorSignInPage />} />
        <Route path="/members" element={<MembersMarketingPage />} />
        <Route path="/clubs" element={<ClubsMarketingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/pricing/clubs" element={<ClubPricingPage />} />
        <Route path="/pricing/vendors" element={<VendorPricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* --- Prompt 3: Vendor placeholder dashboard --- */}
        <Route path="/vendor" element={<ProtectedRoute role="vendor"><VendorDashboardPage /></ProtectedRoute>} />
        <Route path="/vendor/requests" element={<ProtectedRoute role="vendor"><VendorRequestsPage /></ProtectedRoute>} />
        <Route path="/vendor/requests/:id" element={<ProtectedRoute role="vendor"><VendorRequestDetailPage /></ProtectedRoute>} />
        <Route path="/vendor/catalog" element={<Navigate to="/vendor/catalogs" replace />} />
        <Route path="/vendor/catalogs" element={<ProtectedRoute role="vendor"><VendorCatalogsPage /></ProtectedRoute>} />
        <Route path="/vendor/catalogs/:catalogId" element={<ProtectedRoute role="vendor"><VendorCatalogDetailPage /></ProtectedRoute>} />
        <Route path="/vendor/orders" element={<ProtectedRoute role="vendor"><VendorRequestsPage archived={true} /></ProtectedRoute>} />
        <Route path="/vendor/messages" element={<ProtectedRoute role="vendor"><VendorMessagesPage /></ProtectedRoute>} />
        <Route path="/vendor/analytics" element={<ProtectedRoute role="vendor"><VendorPlaceholderPage title="Analytics" /></ProtectedRoute>} />
        <Route path="/vendor/marketing" element={<ProtectedRoute role="vendor"><VendorPlaceholderPage title="Marketing" /></ProtectedRoute>} />
        <Route path="/vendor/profile" element={<ProtectedRoute role="vendor"><VendorCompanyProfilePage /></ProtectedRoute>} />
        <Route path="/vendor/settings" element={<ProtectedRoute role="vendor"><VendorSettingsPage /></ProtectedRoute>} />

        {/* --- SA Vendor Approval Queue --- */}
        <Route path="/super-admin/vendors" element={<ProtectedRoute role="super_admin"><SuperAdminVendorsPage /></ProtectedRoute>} />
        {/* iter72j · Support ticket routes */}
        <Route path="/super-admin/support" element={<ProtectedRoute role="super_admin"><SuperAdminSupportPage /></ProtectedRoute>} />
        <Route path="/super-admin/support/:ticketId" element={<ProtectedRoute role="super_admin"><SupportTicketDetailPage isSuperAdminView={true} /></ProtectedRoute>} />
        <Route path="/support/tickets" element={<ProtectedRoute><MyTicketsPage /></ProtectedRoute>} />
        <Route path="/support/tickets/:ticketId" element={<ProtectedRoute><SupportTicketDetailPage /></ProtectedRoute>} />

        {/* --- iter63 · Catalog + Collection UI (Phase 1B) --- */}
        <Route path="/admin/catalogs" element={<ProtectedRoute role="club_admin"><AdminCatalogsPage /></ProtectedRoute>} />
        <Route path="/admin/catalogs/:catalogId" element={<ProtectedRoute role="club_admin"><AdminCatalogViewPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute role="club_admin"><AdminReportsPage /></ProtectedRoute>} />
        <Route path="/collections" element={<ProtectedRoute role="member"><MemberCollectionsPage /></ProtectedRoute>} />
        <Route path="/collections/:collectionId" element={<ProtectedRoute role="member"><MemberCollectionDetailPage /></ProtectedRoute>} />
        <Route path="/collections/:collectionId/products/:productId" element={<ProtectedRoute role="member"><MemberProductDetailPage /></ProtectedRoute>} />

        {/* --- Command Center placeholder modules (Build Guide Phase 4) --- */}
        <Route path="/super-admin/members" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-members" title="Members" blurb="A platform-wide view of every member across every club — searchable by name, email, club, or membership status. Merge duplicates and drill into audit trails from a single directory." /></ProtectedRoute>} />
        <Route path="/super-admin/marketplace" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-marketplace" title="Vendor Marketplace" blurb="Browse approved vendor catalogs, feature specific brands, and toggle categories on/off for the club-facing shop-by-vendor experience." /></ProtectedRoute>} />
        <Route path="/super-admin/revenue" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-revenue" title="Revenue" blurb="Track subscription revenue, per-club MRR, vendor tier ARPU, and payout schedules once Stripe billing launches." /></ProtectedRoute>} />
        <Route path="/super-admin/support" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-support" title="Support" blurb="Inbound support tickets from clubs, vendors, and members — SLA-managed, with assignment and escalation flows built for a small platform team." /></ProtectedRoute>} />
        <Route path="/super-admin/settings" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-settings" title="Settings" blurb="Platform-wide toggles: email sender identity, feature flags, master override rotation, brand assets, and default club configuration." /></ProtectedRoute>} />
        <Route path="/super-admin/audit" element={<ProtectedRoute role="super_admin"><CommandCenterPlaceholder testId="cc-audit-logs" title="Audit Logs" blurb="The full audit log, filterable by actor, action, target, club, and date range — with CSV export. The dashboard tile shows the latest 200; this page will surface the full history." /></ProtectedRoute>} />

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<RootRedirect />} />
      </Routes>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
